/**
 * Turn a user's access off, or back on.
 *
 * Disabling signs them out of every open session as well as blocking sign-in. A block on
 * its own only bites at the next sign-in, so someone with a live session would keep
 * working for up to thirty days — which is not what anyone means by "disable".
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { UserRow } from "@/hooks/use-users";
import { useSetAccess } from "@/hooks/use-users";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserRow;
}

export function AccessDialog({ open, onOpenChange, user }: Props) {
  const disabling = user.accessStatus === "active";
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const setAccess = useSetAccess();

  function handleSubmit() {
    setAccess.mutate(
      {
        userId: user.id,
        status: disabling ? "disabled" : "active",
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        // A date input gives a bare day; read it as the end of that day in the viewer's
        // own zone, which is what "until the 14th" means to the person typing it.
        ...(disabling && until ? { expiresAt: new Date(`${until}T23:59:59`).toISOString() } : {}),
      },
      {
        onSuccess: () => {
          setReason("");
          setUntil("");
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{disabling ? "Disable access" : "Restore access"}</DialogTitle>
          <DialogDescription>
            {disabling
              ? `${user.name} will be signed out everywhere and will not be able to sign back in.`
              : `${user.name} will be able to sign in again.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="access-reason">Reason</Label>
            <Textarea
              id="access-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={disabling ? "e.g. left the company" : "e.g. returned from leave"}
              rows={2}
            />
          </div>

          {disabling && (
            <div className="space-y-2">
              <Label htmlFor="access-until">Until (optional)</Label>
              <Input
                id="access-until"
                type="date"
                value={until}
                onChange={(event) => setUntil(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to disable indefinitely. With a date, access returns on its own.
              </p>
            </div>
          )}

          {disabling && user.activeSessionCount > 0 && (
            <p className="text-xs text-muted-foreground">
              {user.activeSessionCount === 1
                ? "1 open session will be ended."
                : `${user.activeSessionCount} open sessions will be ended.`}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={disabling ? "destructive" : "default"}
            onClick={handleSubmit}
            disabled={setAccess.isPending}
          >
            {setAccess.isPending ? "Saving…" : disabling ? "Disable access" : "Restore access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
